/*
 * Premium motion — layer 1 of 3: scroll.
 *
 * Reveals elements as they enter the viewport, splits headings into animatable
 * characters and staggers drawer rows. It draws nothing of its own — the
 * header is left exactly as the theme renders it at every scroll position.
 *
 * The selectors in REVEAL_MAP are mirrored by the pre-paint hidden-state block
 * in premium-motion.css. Add to one, add to the other, or the element will
 * either flash in or never hide.
 */
(function () {
  'use strict';

  const root = document.documentElement;
  if (!root.classList.contains('pm-motion')) return;

  /*
   * Selector -> reveal variant. `up` is the default rise-and-fade; `mask` and
   * `wipe` are clip-path openings for editorial imagery; `left`/`right` are for
   * side-by-side layouts; `scale` is a gentle push-in for cards.
   */
  const REVEAL_MAP = [
    ['.pm-heading', 'up'],
    /* Hero. Above the fold, so these fire on load rather than on scroll. */
    ['.bannerlatest .content h1', 'up'],
    ['.bannerlatest .content > a', 'up'],
    ['.bannerlatest .testimonial-content', 'up'],
    ['.scroll-indicator', 'up'],
    ['.headingmaequee', 'up'],
    ['.main-heading', 'up'],
    ['.product_page_haeding', 'up'],
    ['.rich-text__heading', 'up'],
    ['.rich-text__text', 'up'],
    ['.rich-text__buttons', 'up'],
    ['.banner__box', 'up'],
    ['.multicolumn-list__item', 'scale'],
    ['.multicolumn__title', 'up'],
    ['.benefits-section .custom-heading', 'up'],
    ['.benefits-section .subtitle', 'up'],
    ['.benefits-section .benefit-box', 'up'],
    ['.benefits-section .bottle-image', 'scale'],
    ['.comparison-section .comparison-content', 'left'],
    ['.comparison-section .comparison-table', 'right'],
    ['.seo-faq-section__header', 'up'],
    ['.seo-faq-section__item', 'up'],
    ['.shop-section .product-card', 'scale'],
    ['.product-grid > .grid__item', 'scale'],
    ['.collection-list > .grid__item', 'scale'],
    ['.article-card-wrapper', 'scale'],
    ['.contact__fields', 'up'],
    ['.main-page-title', 'up'],
    ['.article-template__title', 'up'],
    ['.cart-item', 'up'],
    /*
     * The media column is deliberately left out: it holds the fullscreen media
     * modal, and a transform on its ancestor would trap that modal inside the
     * section for as long as the reveal is settling.
     */
    ['.product__info-wrapper', 'right'],
    ['.footer-nav', 'up'],
    ['.footer-socials', 'up'],
    ['.footer-email', 'up'],
    ['[data-pm-reveal]', null],
  ];

  const REVEAL_SELECTOR = REVEAL_MAP.map((entry) => entry[0]).join(',');

  /* Headings whose text gets split into per-character spans. */
  const SPLIT_SELECTOR = [
    '.pm-heading',
    '.bannerlatest .content h1',
    '.headingmaequee',
    '.main-heading',
    '.product_page_haeding',
    '.rich-text__heading',
    '.multicolumn__title .title',
    '.benefits-section .custom-heading',
    '.comparison-section .custom-heading',
    '.seo-faq-section__heading',
    '.main-page-title',
    '.article-template__title',
    '.product__title h1',
    '.collection-hero__title',
  ].join(',');

  const STAGGER_MS = 65;
  const MAX_STAGGER_STEPS = 6;
  const CHAR_STAGGER_MS = 22;
  /* Past this length a character cascade reads as noise, so the heading just rises. */
  const MAX_SPLIT_LENGTH = 90;
  const SETTLE_MS = 1400;
  /*
   * The transition curtain takes about this long to clear on arrival. Reveals
   * that fire in that window wait it out, otherwise everything above the fold
   * finishes animating behind a curtain nobody has seen lift yet.
   *
   * Tracks COLUMN_DURATION_MS in premium-transitions.js — the point at which
   * the leading column has fully lifted and the content behind it is on show.
   * It came down with that value; every reveal above the fold was sitting on
   * this timer, so it was costing the page its first impression.
   */
  const CURTAIN_MS = 380;
  const startedAt = performance.now();

  /* ------------------------------------------------------------- splitting */

  function splitTextNode(node) {
    const text = node.nodeValue;
    if (!text.trim()) return null;

    const fragment = document.createDocumentFragment();

    // Split on whitespace but keep it, so wrapping and spacing survive intact.
    text.split(/(\s+)/).forEach((chunk) => {
      if (!chunk) return;

      if (/^\s+$/.test(chunk)) {
        fragment.appendChild(document.createTextNode(chunk));
        return;
      }

      const word = document.createElement('span');
      word.className = 'pm-word';

      Array.from(chunk).forEach((character) => {
        const span = document.createElement('span');
        span.className = 'pm-char';
        span.textContent = character;
        word.appendChild(span);
      });

      fragment.appendChild(word);
    });

    return fragment;
  }

  /*
   * Walks text nodes only, so inline markup the theme relies on for accent
   * colouring (`<strong>`, `<span>`) survives the split untouched.
   */
  function collectTextNodes(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (node.parentElement.closest('.pm-word')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    let current = walker.nextNode();
    while (current) {
      nodes.push(current);
      current = walker.nextNode();
    }
    return nodes;
  }

  function split(element) {
    if (element.hasAttribute('data-pm-split')) return;

    const label = element.textContent.replace(/\s+/g, ' ').trim();
    if (!label || label.length > MAX_SPLIT_LENGTH) return;

    const textNodes = collectTextNodes(element);
    if (textNodes.length === 0) return;

    textNodes.forEach((node) => {
      const replacement = splitTextNode(node);
      if (replacement) node.parentNode.replaceChild(replacement, node);
    });

    // Characters read one-by-one to a screen reader, so hand it the whole
    // string instead and hide the pieces.
    element.setAttribute('aria-label', label);
    element.querySelectorAll('.pm-word').forEach((word) => word.setAttribute('aria-hidden', 'true'));

    element.querySelectorAll('.pm-char').forEach((character, index) => {
      character.style.setProperty('--pm-char-delay', `${index * CHAR_STAGGER_MS}ms`);
    });

    element.setAttribute('data-pm-split', '');
  }

  /* -------------------------------------------------------------- revealing */

  function variantFor(element) {
    for (let i = 0; i < REVEAL_MAP.length; i += 1) {
      const [selector, variant] = REVEAL_MAP[i];
      if (variant && element.matches(selector)) return variant;
    }
    return null;
  }

  function reveal(element, stagger) {
    if (element.hasAttribute('data-pm-in')) return;

    const waitForCurtain = Math.max(0, CURTAIN_MS - (performance.now() - startedAt));
    const delay = Math.round(stagger + waitForCurtain);

    element.style.setProperty('--pm-delay', `${delay}ms`);
    element.setAttribute('data-pm-in', '');

    // Hand the element back to the fast hover timings once it has landed, and
    // drop the compositing hint. Inline, so it wins on specificity.
    const charCount = element.querySelectorAll('.pm-char').length;
    const settleAfter = delay + SETTLE_MS + charCount * CHAR_STAGGER_MS;

    window.setTimeout(() => {
      element.style.setProperty('--pm-delay', '0ms');
      element.style.setProperty('--pm-tdur', 'var(--pm-hover-duration)');
      element.style.willChange = 'auto';

      /*
       * A transformed element is a containing block for `position: fixed`,
       * which would trap product media modals and quick-add dialogs inside
       * their section. Elements that only ever revealed can drop the transform
       * entirely once they have landed; the ones the pointer layer drives keep
       * it, and are chosen in premium-interactions.js to never wrap a modal.
       */
      if (!element.hasAttribute('data-pm-card') && !element.hasAttribute('data-pm-press')) {
        element.style.transform = 'none';
      }
    }, settleAfter);
  }

  /*
   * Siblings entering together cascade rather than popping as one slab.
   */
  function staggerIndex(element) {
    const parent = element.parentElement;
    if (!parent) return 0;

    const siblings = Array.prototype.filter.call(parent.children, (child) =>
      child.matches(REVEAL_SELECTOR)
    );
    if (siblings.length < 2) return 0;

    return Math.min(siblings.indexOf(element), MAX_STAGGER_STEPS);
  }

  function prepare(element) {
    if (element.hasAttribute('data-pm-tx')) return false;

    element.setAttribute('data-pm-tx', '');

    if (!element.hasAttribute('data-pm-variant')) {
      const variant = element.getAttribute('data-pm-reveal') || variantFor(element);
      if (variant) element.setAttribute('data-pm-variant', variant);
    }

    // A heading is often nested inside the block that reveals it, so look both
    // at the element and under it.
    if (element.matches(SPLIT_SELECTOR)) split(element);
    element.querySelectorAll(SPLIT_SELECTOR).forEach(split);

    return true;
  }

  let observer = null;

  function observe(scope) {
    const elements = scope.querySelectorAll(REVEAL_SELECTOR);
    if (elements.length === 0) return;

    elements.forEach((element) => {
      if (prepare(element) && observer) observer.observe(element);
    });
  }

  function revealEverything(scope) {
    scope.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
      element.setAttribute('data-pm-tx', '');
      element.setAttribute('data-pm-in', '');
    });
  }

  /* --------------------------------------------------------- drawer stagger */

  /*
   * The drawers are custom elements the theme opens by toggling `open` on a
   * <details> or a class on the host, so watching attributes is the one hook
   * that works for both without patching their scripts.
   */
  function initDrawers() {
    const drawers = document.querySelectorAll('.menu-drawer, cart-drawer.cart-drawer');
    if (drawers.length === 0) return;

    drawers.forEach((drawer) => {
      const host = drawer.closest('details') || drawer;

      function sync() {
        // The menu drawer is a <details>; the cart drawer flips an `active`
        // class on its host element.
        const isOpen = host.hasAttribute('open') || drawer.classList.contains('active');
        const rows = drawer.querySelectorAll('.menu-drawer__menu > li, .cart-item');

        rows.forEach((row, index) => row.style.setProperty('--pm-row', index));
        drawer.setAttribute('data-pm-drawer', isOpen ? 'open' : 'closed');
      }

      new MutationObserver(sync).observe(host, {
        attributes: true,
        attributeFilter: ['open', 'class'],
      });

      sync();
    });
  }

  /* -------------------------------------------------------------- bootstrap */

  /* ---------------------------------------------------------- safety net */

  /*
   * Nothing on screen may stay hidden. If an element is sitting in the
   * viewport and still has not been revealed — an IntersectionObserver
   * callback that never fired, a zero-area box, a section swapped in behind
   * the observer — this reveals it outright.
   *
   * It only touches what is already on screen, so reveals further down the
   * page still play on scroll as intended.
   */
  function rescueVisible() {
    document.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
      if (element.hasAttribute('data-pm-in')) return;

      const box = element.getBoundingClientRect();
      if (box.top > window.innerHeight || box.bottom < 0) return;

      reveal(element, 0);
      if (observer) observer.unobserve(element);
    });
  }

  function init() {
    if (!('IntersectionObserver' in window)) {
      revealEverything(document);
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target, staggerIndex(entry.target) * STAGGER_MS);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
    );

    observe(document);
    initDrawers();

    // Sections rendered later (quick add, cart re-render, infinite scroll) and
    // sections the editor swaps in.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) observe(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', (event) => {
      observe(event.target);
      window.setTimeout(rescueVisible, 60);
    });

    window.addEventListener('load', () => window.setTimeout(rescueVisible, 1200));
    window.setTimeout(rescueVisible, 3000);
  }

  /*
   * Claimed at parse time, not after init. The failsafe in theme.liquid fires
   * on a timer, and on a slow connection DOMContentLoaded can land after it —
   * which would strip `.pm-motion` from a page that was about to animate
   * perfectly well. Errors during init are handled by the catch below instead.
   */
  window.premiumMotionReady = true;

  function boot() {
    try {
      init();
    } catch (error) {
      /*
       * A thrown error must never leave the page hidden. Drop `.pm-motion` so
       * every pre-paint hidden state stops applying and the page renders
       * unanimated, then let the error surface.
       */
      revealEverything(document);
      root.classList.remove('pm-motion');
      throw error;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
